<template>
  <div class="footer-newsletter" @keyup.enter="submitRequestIfValid">
    <div class="footer-newsletter__title">{{ $t('title') }}</div>
    <div v-if="!isSignupRequestSent" class="footer-newsletter__signup-form">
      <div class="footer-newsletter__copy">{{ $t('copy') }}</div>
      <EmailFormInput
        id="email"
        ref="email"
        class="footer-newsletter__input"
        :isDisabled="isSubmitting"
        :placeholder="$t('email')"
        :showMessageOn="showMessageOn"
        :value.sync="emailAddress"
        @isValid="setEmailValidity"
        v-data-test-id="'email-input'"
      />
      <transition name="fade">
        <div
          v-if="isEmailValid"
          class="footer-newsletter__submit-container"
          v-data-test-id="'submit-container'"
        >
          <FormSelect
            id="gender"
            class="footer-newsletter__gender-selector"
            :value="selectedGender"
            :options="genderOptions"
            @update:value="updateSelectedGender($event)"
          />
          <Button
            class="footer-newsletter__submit"
            :height="BUTTON_STYLES.height.small"
            :color="BUTTON_STYLES.color.white"
            :isSubmitting="isSubmitting"
            @click.prevent="$newsletterSignup_submit(formData)"
          >
            {{ $t('subscribe') }}
          </Button>
        </div>
      </transition>
    </div>
    <transition name="fade">
      <div
        v-if="isSignupRequestSent"
        class="footer-newsletter__submit-feedback"
        v-data-test-id="'submit-feedback'"
      >
        {{ this.isSuccess ? $t('success') : $t('error') }}
        <Button
          v-if="isError"
          class="footer-newsletter__retry"
          :height="BUTTON_STYLES.height.small"
          :color="BUTTON_STYLES.color.white"
          :isSubmitting="isSubmitting"
          @click.prevent="isError = false"
          v-data-test-id="'retry-button'"
        >
          {{ $t('try_again') }}
        </Button>
      </div>
    </transition>
  </div>
</template>

<script>
import newsletterSignupMixin, { SOURCES } from '@/mixins/newsletter-signup'
import { EVENT_TYPES } from '@/components/_shared/Form/Validation/FieldValidator.vue'
import { TYPES as VALIDATION_TYPES } from '@/components/_shared/Form/Validation/FieldValidation.vue'
import FormSelect from '@/components/_shared/Form/Select/FormSelect.vue'
import EmailFormInput from '@/components/_shared/Form/Input/EmailFormInput.vue'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'

export default {
  name: 'FooterNewsletter',
  components: { Button, EmailFormInput, FormSelect },
  mixins: [newsletterSignupMixin],

  data() {
    return {
      BUTTON_STYLES,
      emailAddress: '',
      genderOptions: [
        { text: this.$t('subscribe_shopping'), value: '' },
        { text: this.$t('subscribe_mens'), value: 'male' },
        { text: this.$t('subscribe_womens'), value: 'female' },
        { text: this.$t('subscribe_all'), value: 'other' },
      ],
      isEmailValid: false,
      isError: false,
      isSubmitting: false,
      isSuccess: false,
      selectedGender: '',
      showMessageOn: {
        [EVENT_TYPES.blur]: [VALIDATION_TYPES.error, VALIDATION_TYPES.warning],
      },
    }
  },

  computed: {
    formData() {
      return {
        email: this.emailAddress,
        gender: this.selectedGender,
        source: SOURCES.footer,
      }
    },

    isSignupRequestSent() {
      return this.isSuccess || this.isError
    },
  },

  methods: {
    updateSelectedGender(value) {
      this.selectedGender = value
    },

    setEmailValidity(isValid) {
      this.isEmailValid = isValid
    },

    submitRequestIfValid() {
      if (this.isEmailValid) this.$newsletterSignup_submit(this.formData)
    },

    $newsletterSignup_onSuccess() {
      this.isSuccess = true
    },

    $newsletterSignup_onError() {
      this.isError = true
    },
  },
}
</script>

<style scoped lang="scss" scooped>
@import './footer-settings';

.footer-newsletter {
  font-family: $font-family-helvetica-now;

  @media ($mq-medium) {
    max-width: $left-column-width-small;
  }

  @media (min-width: $mobile-bp) {
    padding: 0 60px 20px 0;
  }

  @media (min-width: $tablet-bp) {
    padding: 0 100px 20px 0;
  }

  @media ($mq-high) {
    max-width: initial;
    width: $left-column-width-high;
  }

  &__title {
    margin-bottom: 16px;
    font-weight: bold;
    font-size: $f-size-xlarge;
    line-height: 1.5;
    letter-spacing: 0.2px;
  }

  &__copy {
    font-size: 14px;
    line-height: 1.5;
  }

  &__submit-container {
    display: flex;
    margin-top: 12px;
    height: 48px;
  }

  &__gender-selector {
    flex: 1;
    margin: 0 12px 0 0;
    background-color: $color-grey-dark-2;
  }

  &__submit {
    flex: 1;
    max-width: 140px;
    font-size: $f-size-normal;
  }

  &__input {
    margin-top: 20px;
  }

  &__submit-feedback {
    padding: 20px 0;
  }

  &__retry {
    display: block;
    width: initial;
    margin-top: 20px;
  }

  /deep/ .input__field {
    color: $c-white;
    border-color: $c-grey-mid-1;

    &:focus {
      border-color: $c-white;
    }
  }

  /deep/ .input__placeholder {
    color: $c-grey-mid-1;
  }

  /deep/ .select__field {
    border: none;
    color: $c-white;
    font-size: $f-size-normal;
  }

  /deep/ .icon-chevron-new {
    color: $c-white;
  }

  /deep/ input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus {
    -webkit-text-fill-color: $c-white;
    -webkit-box-shadow: 0 0 0px 1000px #000 inset;
    transition: background-color 5000s ease-in-out 0s;
  }

  @include internet-explorer-only() {
    /deep/ option {
      color: $color-black;
    }
  }

  .fade-leave-active {
    display: none;
  }

  .fade-enter-active {
    transition: opacity 0.5s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>

<i18n src="@/locales/footer_newsletter.json">
</i18n>
